import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/app/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const { refresh } = useAuth();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 15) {
        setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setTimeout(poll, 2000);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="pay-success-page">
        {status === "polling" && (
          <>
            <Clock className="w-16 h-16 mx-auto text-[#ffb800] animate-pulse" />
            <h1 className="mt-6 font-display font-bold text-4xl tracking-tight">Confirming payment…</h1>
            <p className="text-zinc-400 mt-4">Hold tight. Adding scans to your account.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-[#00ff66]" data-testid="pay-success-icon" />
            <h1 className="mt-6 font-display font-bold text-4xl tracking-tight">Payment complete.</h1>
            <p className="text-zinc-300 mt-4">
              <span className="font-mono text-[#10b981] text-xl">+{data?.scans_added}</span> scans added.
              You now have <span className="font-mono text-[#10b981] text-xl">{data?.user_scans_remaining}</span> total.
            </p>
            <Link to="/dashboard">
              <Button className="mt-8 rounded-lg bg-[#10b981] hover:bg-[#059669] font-semibold h-12 px-8" data-testid="success-go-dashboard">
                Start Scanning
              </Button>
            </Link>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-[#ff3b30]" />
            <h1 className="mt-6 font-display font-bold text-4xl tracking-tight">Something went wrong</h1>
            <p className="text-zinc-400 mt-4">
              {status === "timeout"
                ? "Timed out checking payment. Refresh or contact support if you were charged."
                : "Payment was not completed."}
            </p>
            <Link to="/pricing">
              <Button className="mt-8 rounded-lg border border-white/20 bg-transparent hover:bg-white/10 font-semibold h-12 px-8">
                Back to Pricing
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
